import { zodResolver } from '@hookform/resolvers/zod';
import { rolesControllerFindAll, usersControllerCreate } from '@kalba/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronDown,
  DollarSign,
  FileCheck,
  Info,
  Mail,
  RefreshCw,
  Save,
  Search,
  Settings,
  Shield,
  SlidersHorizontal,
  Star,
  TrendingUp,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { FormField } from '../../components/forms/form-field';
import { FormSection } from '../../components/forms/form-section';
import { AppLayout } from '../../components/layout/app-layout';
import { Input } from '../../components/ui/input';
import { PhoneInput } from '../../components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';
import { type NewUserFormValues, newUserSchema } from '../../lib/validations';
import { useAuthStore } from '../../stores/auth.store';

export const Route = createFileRoute('/users/new')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('user:create')) throw redirect({ to: '/dashboard' });
  },
  component: NewUserPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface Role {
  id: string;
  name: string;
  description?: string;
}

// ── Role colors ───────────────────────────────────────────────────────────────

const ROLE_COLOR_SEEDS: Array<{
  key: string;
  color: string;
  bg: string;
  border: string;
  dot: string;
}> = [
  {
    key: 'admin',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    dot: 'bg-indigo-500',
  },
  {
    key: 'inspector',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
  },
  {
    key: 'finance',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    dot: 'bg-blue-500',
  },
  {
    key: 'customer',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
  },
  {
    key: 'operation',
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    dot: 'bg-orange-500',
  },
  {
    key: 'super',
    color: 'text-violet-700',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    dot: 'bg-violet-500',
  },
  {
    key: 'viewer',
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    border: 'border-gray-200',
    dot: 'bg-gray-400',
  },
];

const FALLBACK_COLORS = [
  { color: 'text-teal-700', bg: 'bg-teal-50', border: 'border-teal-200', dot: 'bg-teal-500' },
  { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', dot: 'bg-rose-500' },
  { color: 'text-cyan-700', bg: 'bg-cyan-50', border: 'border-cyan-200', dot: 'bg-cyan-500' },
  { color: 'text-pink-700', bg: 'bg-pink-50', border: 'border-pink-200', dot: 'bg-pink-500' },
];

function getRoleColor(name: string, index: number) {
  const lower = name.toLowerCase();
  const seed = ROLE_COLOR_SEEDS.find((s) => lower.includes(s.key));
  if (seed) return seed;
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function hashPassword(plain: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

const DEPARTMENTS = ['Operations', 'Finance', 'Legal', 'IT', 'Sales', 'HR', 'Engineering'];

// ── Sub-components ────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-3.5">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={cn(
          'relative w-11 h-6 rounded-full transition-all duration-200 shrink-0 focus:outline-none focus:ring-2 focus:ring-primary/30',
          checked ? 'bg-primary shadow-md shadow-primary/30' : 'bg-border',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200',
            checked ? 'translate-x-5' : 'translate-x-0',
          )}
        />
      </button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function NewUserPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canManageOverrides = can('user:manage-overrides');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<NewUserFormValues>({
    resolver: zodResolver(newUserSchema),
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      employeeId: '',
      department: '',
      password: '',
      sendInvite: true,
      forceReset: true,
    },
  });

  // Keep a local form alias for read access in existing code below
  const form = {
    fullName: watch('fullName'),
    email: watch('email'),
    phone: watch('phone'),
    employeeId: watch('employeeId') ?? '',
    department: watch('department') ?? '',
    password: watch('password') ?? '',
    sendInvite: watch('sendInvite'),
    forceReset: watch('forceReset'),
  };
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [roleSearch, setRoleSearch] = useState('');
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [moduleAccess, setModuleAccess] = useState<Record<string, 'none' | 'read' | 'write'>>({
    Permits: 'none',
    Customers: 'none',
    Payments: 'none',
    Reports: 'none',
    Settings: 'none',
  });
  const [success, setSuccess] = useState(false);
  const [createdName, setCreatedName] = useState('');

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesControllerFindAll() as unknown as Promise<Role[]>,
  });

  const selectedRoles = roles.filter((r) => selectedRoleIds.includes(r.id));
  const availableRoles = roles.filter(
    (r) =>
      !selectedRoleIds.includes(r.id) && r.name.toLowerCase().includes(roleSearch.toLowerCase()),
  );

  const isFormValid = !!(
    form.fullName &&
    form.email &&
    !errors.email &&
    selectedRoleIds.length > 0
  );

  const createMutation = useMutation({
    mutationFn: async (data: NewUserFormValues) => {
      const passwordHash = await hashPassword(data.password || 'ChangeMe123!');
      const created = (await usersControllerCreate({
        requestBody: {
          name: data.fullName,
          email: data.email,
          username: data.username || undefined,
          phoneNumber: data.phone || undefined,
          passwordHash,
          roleIds: selectedRoleIds,
        },
      })) as unknown as { id: string };
      return created;
    },
    onSuccess: (data) => {
      setCreatedName(form.fullName);
      setSuccess(true);
      setTimeout(() => {
        navigate({ to: '/users/$userId', params: { userId: data.id } });
      }, 1800);
    },
    onError: (err: unknown) => {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const raw = body?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to create user.'];
      toast.error('Failed to create user', { description: lines.join(' · '), duration: 5000 });
    },
  });

  const progressSteps = [
    { label: 'Basic Info', done: !!(form.fullName && form.email) },
    { label: 'Auth', done: !!(form.password || form.sendInvite) },
    { label: 'Roles', done: selectedRoleIds.length > 0 },
  ];

  // ── Success state ──────────────────────────────────────────────────────────

  if (success) {
    return (
      <AppLayout noPadding>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)] p-8">
          <div className="w-full max-w-lg text-center space-y-6">
            <div className="relative mx-auto w-24 h-24">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary to-emerald-500 flex items-center justify-center shadow-2xl shadow-primary/30 mx-auto">
                <Check className="w-12 h-12 text-white" />
              </div>
              <div className="absolute inset-0 rounded-3xl bg-primary/20 blur-xl -z-10" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">User Created!</h2>
              <p className="text-muted-foreground mt-2">
                <span className="font-semibold text-foreground">{createdName}</span> has been added
                to the system.
              </p>
            </div>
            <p className="text-sm text-muted-foreground">Redirecting to user profile…</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────────

  return (
    <AppLayout noPadding>
      <div className="flex flex-col min-h-[calc(100vh-4rem)]">
        {/* ── Header ── */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
            <span>Settings</span>
            <span>/</span>
            <button
              type="button"
              onClick={() => navigate({ to: '/users' })}
              className="hover:text-foreground transition-colors"
            >
              Users
            </button>
            <span>/</span>
            <span className="text-foreground font-medium">Create New User</span>
          </nav>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/30 shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Create New User</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Create system users and assign permissions
                </p>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => navigate({ to: '/users' })}
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-500 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 hover:shadow-sm transition-all text-sm font-medium text-gray-700 flex items-center gap-2"
              >
                <Save className="w-4 h-4 text-gray-400" /> Save Draft
              </button>
              <button
                type="button"
                disabled={!isFormValid || createMutation.isPending}
                onClick={handleSubmit((data) => createMutation.mutate(data)) as () => void}
                className={cn(
                  'px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-2',
                  isFormValid && !createMutation.isPending
                    ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-200'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed',
                )}
              >
                {createMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Creating…
                  </>
                ) : (
                  <>
                    <User className="w-4 h-4" /> Create User
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Progress steps */}
          <div className="flex items-center gap-6 mt-5">
            {progressSteps.map((step, i) => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className={cn(
                    'w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all duration-300',
                    step.done ? 'border-primary bg-primary' : 'border-border bg-background',
                  )}
                >
                  {step.done && <Check className="w-3 h-3 text-white" />}
                </div>
                <span
                  className={cn(
                    'text-xs font-medium',
                    step.done ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {step.label}
                </span>
                {i < progressSteps.length - 1 && (
                  <div className={cn('w-10 h-px', step.done ? 'bg-primary/40' : 'bg-border')} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="flex flex-1 min-h-0">
          {/* Left: form */}
          <div className="flex-1 px-8 py-7 pb-28 space-y-5 overflow-y-auto min-w-0">
            {/* Section 1: Basic Information */}
            <FormSection
              icon={<User className="w-4 h-4" />}
              iconBg="bg-secondary/10 text-secondary"
              title="Basic Information"
              subtitle="Identity and contact details"
            >
              <div className="grid grid-cols-2 gap-5">
                {/* Full Name (full width) */}
                <FormField
                  className="col-span-2"
                  label="Full Name"
                  required
                  error={errors.fullName?.message}
                >
                  <Controller
                    control={control}
                    name="fullName"
                    render={({ field }) => (
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="First and last name"
                          hasError={!!errors.fullName}
                          className="pl-10"
                        />
                      </div>
                    )}
                  />
                  {form.fullName && !errors.fullName && (
                    <p className="text-xs text-gray-500 mt-1.5">
                      Display name:{' '}
                      <span className="font-semibold text-gray-900">{form.fullName}</span>
                    </p>
                  )}
                </FormField>

                {/* Email */}
                <FormField label="Email Address" required error={errors.email?.message}>
                  <Controller
                    control={control}
                    name="email"
                    render={({ field }) => (
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="email"
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="user@company.com"
                          hasError={!!errors.email}
                          className="pl-10"
                        />
                      </div>
                    )}
                  />
                </FormField>

                {/* Username */}
                <FormField label="Username" hint="Optional" error={errors.username?.message}>
                  <Controller
                    control={control}
                    name="username"
                    render={({ field }) => (
                      <Input
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="e.g. janesmith"
                        hasError={!!errors.username}
                      />
                    )}
                  />
                </FormField>

                {/* Phone */}
                <FormField label="Phone Number" error={errors.phone?.message}>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field }) => (
                      <PhoneInput
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="502305316"
                        hasError={!!errors.phone}
                      />
                    )}
                  />
                </FormField>

                {/* Employee ID */}
                <FormField label="Employee ID" hint="Optional">
                  <Controller
                    control={control}
                    name="employeeId"
                    render={({ field }) => (
                      <Input
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="e.g. EMP-00471"
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500 mt-1.5">Used for internal HR system sync</p>
                </FormField>

                {/* Department */}
                <FormField label="Department">
                  <Controller
                    control={control}
                    name="department"
                    render={({ field }) => (
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select department…" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEPARTMENTS.map((d) => (
                            <SelectItem key={d} value={d}>
                              {d}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>
              </div>
            </FormSection>

            {/* Section 2: Authentication */}
            <FormSection
              icon={<Shield className="w-4 h-4" />}
              iconBg="bg-primary/10 text-primary"
              title="Authentication"
              subtitle="Password and access settings"
            >
              <div className="space-y-4">
                <FormField
                  label="Temporary Password"
                  hint={form.sendInvite ? 'Optional — user will set their own' : 'Required'}
                >
                  <Controller
                    control={control}
                    name="password"
                    render={({ field }) => (
                      <Input
                        type="password"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder={
                          form.sendInvite ? 'Auto-generated on invite' : 'Min 8 characters'
                        }
                      />
                    )}
                  />
                  {form.password && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1 rounded-full bg-border overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            form.password.length < 6
                              ? 'w-1/4 bg-red-500'
                              : form.password.length < 10
                                ? 'w-1/2 bg-warning'
                                : 'w-full bg-success',
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          'text-xs font-medium',
                          form.password.length < 6
                            ? 'text-red-500'
                            : form.password.length < 10
                              ? 'text-warning'
                              : 'text-success',
                        )}
                      >
                        {form.password.length < 6
                          ? 'Weak'
                          : form.password.length < 10
                            ? 'Fair'
                            : 'Strong'}
                      </span>
                    </div>
                  )}
                </FormField>

                <div className="rounded-2xl border border-gray-200 bg-gray-50/50 px-4 divide-y divide-gray-100">
                  <Controller
                    control={control}
                    name="sendInvite"
                    render={({ field }) => (
                      <Toggle
                        checked={field.value}
                        onChange={field.onChange}
                        label="Send email invite"
                        description="User receives an activation link to set up their account"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="forceReset"
                    render={({ field }) => (
                      <Toggle
                        checked={field.value}
                        onChange={field.onChange}
                        label="Force password reset on first login"
                        description="Require the user to change their password on first sign-in"
                      />
                    )}
                  />
                </div>
              </div>
            </FormSection>

            {/* Section 3: Roles & Permissions */}
            <FormSection
              className="overflow-visible"
              icon={<Star className="w-4 h-4" />}
              iconBg="bg-warning/10 text-warning"
              title="Roles & Permissions *"
              subtitle="Assign one or more roles to control access"
              badge={
                selectedRoleIds.length > 0 ? (
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                    {selectedRoleIds.length} assigned
                  </span>
                ) : undefined
              }
            >
              <div className="space-y-4">
                {/* Selected role chips */}
                {selectedRoles.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200 min-h-[48px]">
                    {selectedRoles.map((r, i) => {
                      const rc = getRoleColor(r.name, i);
                      return (
                        <span
                          key={r.id}
                          className={cn(
                            'inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full text-xs font-semibold border',
                            rc.bg,
                            rc.color,
                            rc.border,
                          )}
                        >
                          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', rc.dot)} />
                          {r.name}
                          <button
                            type="button"
                            onClick={() => setSelectedRoleIds((p) => p.filter((id) => id !== r.id))}
                            className="w-4 h-4 rounded-full hover:bg-black/10 flex items-center justify-center transition-colors ml-0.5"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Role search */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <input
                      value={roleSearch}
                      onChange={(e) => {
                        setRoleSearch(e.target.value);
                        setRoleDropdownOpen(true);
                      }}
                      onFocus={() => setRoleDropdownOpen(true)}
                      placeholder={
                        selectedRoleIds.length === 0
                          ? 'Search and assign roles…'
                          : 'Add another role…'
                      }
                      className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
                    />
                  </div>

                  {roleDropdownOpen && availableRoles.length > 0 && (
                    <div className="absolute z-20 top-full mt-1.5 w-full bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
                      <div className="p-1.5">
                        {availableRoles.map((r, i) => {
                          const rc = getRoleColor(r.name, i);
                          return (
                            <button
                              key={r.id}
                              type="button"
                              onClick={() => {
                                setSelectedRoleIds((p) => [...p, r.id]);
                                setRoleSearch('');
                                setRoleDropdownOpen(false);
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group"
                            >
                              <span className={cn('w-2 h-2 rounded-full shrink-0', rc.dot)} />
                              <span className="text-sm font-medium flex-1">{r.name}</span>
                              <span
                                className={cn(
                                  'text-xs px-2 py-0.5 rounded-full border opacity-0 group-hover:opacity-100 transition-opacity',
                                  rc.bg,
                                  rc.color,
                                  rc.border,
                                )}
                              >
                                Add
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {roleDropdownOpen && (
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setRoleDropdownOpen(false)}
                    />
                  )}
                </div>

                {/* Quick-select role cards (shown when nothing selected) */}
                {selectedRoleIds.length === 0 && roles.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {roles.slice(0, 4).map((r, i) => {
                      const rc = getRoleColor(r.name, i);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => setSelectedRoleIds([r.id])}
                          className={cn(
                            'flex items-center gap-3 p-3 rounded-xl border hover:shadow-sm transition-all text-left group',
                            rc.border,
                            rc.bg,
                          )}
                        >
                          <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', rc.dot)} />
                          <div className="min-w-0">
                            <p className={cn('text-xs font-semibold', rc.color)}>{r.name}</p>
                            {r.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {r.description}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </FormSection>

            {/* Section 5: Advanced Permissions (collapsible) — only for actors who can grant overrides */}
            {canManageOverrides && (
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((p) => !p)}
                  className="w-full flex items-center justify-between gap-3 px-6 py-4 hover:bg-accent/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center">
                      <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-sm font-semibold">Advanced Permissions</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Module-level access overrides · Optional
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {advancedOpen ? 'Hide' : 'Configure'}
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform duration-200',
                        advancedOpen && 'rotate-180',
                      )}
                    />
                  </div>
                </button>

                {advancedOpen && (
                  <div className="border-t border-border px-6 pb-6 pt-5 space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Override module access independently of assigned roles. Leave as{' '}
                      <span className="font-semibold">None</span> to inherit from roles.
                    </p>
                    <div className="space-y-2">
                      {Object.keys(moduleAccess).map((mod) => (
                        <div
                          key={mod}
                          className="flex items-center justify-between gap-4 py-2 border-b border-border/50 last:border-0"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 rounded-lg bg-accent flex items-center justify-center">
                              {mod === 'Permits' && (
                                <FileCheck className="w-3 h-3 text-muted-foreground" />
                              )}
                              {mod === 'Customers' && (
                                <Users className="w-3 h-3 text-muted-foreground" />
                              )}
                              {mod === 'Payments' && (
                                <DollarSign className="w-3 h-3 text-muted-foreground" />
                              )}
                              {mod === 'Reports' && (
                                <TrendingUp className="w-3 h-3 text-muted-foreground" />
                              )}
                              {mod === 'Settings' && (
                                <Settings className="w-3 h-3 text-muted-foreground" />
                              )}
                            </div>
                            <span className="text-sm font-medium">{mod}</span>
                          </div>
                          <div className="flex items-center gap-1 bg-background rounded-lg p-1 border border-border">
                            {(['none', 'read', 'write'] as const).map((lvl) => (
                              <button
                                key={lvl}
                                type="button"
                                onClick={() => setModuleAccess((p) => ({ ...p, [mod]: lvl }))}
                                className={cn(
                                  'px-3 py-1 rounded-md text-xs font-semibold capitalize transition-all',
                                  moduleAccess[mod] === lvl
                                    ? lvl === 'write'
                                      ? 'bg-primary text-white shadow-sm'
                                      : lvl === 'read'
                                        ? 'bg-secondary/15 text-secondary'
                                        : 'bg-muted text-muted-foreground'
                                    : 'text-muted-foreground hover:bg-accent/60',
                                )}
                              >
                                {lvl}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {createMutation.isError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                Failed to create user. Email may already be in use.
              </div>
            )}
          </div>

          {/* Right: live preview panel */}
          <div className="w-72 shrink-0 px-6 py-7">
            <div className="sticky top-20 space-y-4">
              {/* User preview card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-theme-sm overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Zap className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <p className="font-semibold text-sm">User Preview</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Live summary as you fill in details
                  </p>
                </div>
                <div className="p-5 space-y-4">
                  {/* Avatar + name */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-md shrink-0">
                      {form.fullName ? (
                        <span className="text-white font-bold text-sm">
                          {form.fullName
                            .split(' ')
                            .filter(Boolean)
                            .map((w) => w[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </span>
                      ) : (
                        <User className="w-5 h-5 text-white/70" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">
                        {form.fullName || 'Full Name'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {form.email || 'email@company.com'}
                      </p>
                    </div>
                  </div>

                  {/* Role badges */}
                  {selectedRoles.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Roles
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedRoles.map((r, i) => {
                          const rc = getRoleColor(r.name, i);
                          return (
                            <span
                              key={r.id}
                              className={cn(
                                'px-2 py-0.5 rounded-full text-xs font-semibold border',
                                rc.bg,
                                rc.color,
                                rc.border,
                              )}
                            >
                              {r.name}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Checklist */}
                  <div className="space-y-1.5 pt-2 border-t border-gray-100">
                    {[
                      { label: 'Full name', done: !!form.fullName },
                      { label: 'Email', done: !!form.email && !errors.email },
                      { label: 'Role assigned', done: selectedRoleIds.length > 0 },
                      { label: 'Auth method', done: !!(form.password || form.sendInvite) },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5 py-1">
                        <div
                          className={cn(
                            'w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all',
                            item.done ? 'bg-success' : 'bg-border',
                          )}
                        >
                          {item.done && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span
                          className={cn(
                            'text-xs',
                            item.done ? 'text-foreground font-medium' : 'text-muted-foreground',
                          )}
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* About roles card */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-theme-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-secondary" />
                  <p className="text-xs font-semibold">About roles</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Users can hold multiple roles. Permissions are additive — the most permissive
                  setting applies across all assigned roles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
